import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import "../App.css";

export default function HelpFAQPage() {
  const { id } = useParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [openIndex, setOpenIndex] = useState(null);
  const faqRefs = useRef([]);

  const faqs = [
    {
        question: "What is Google Play Review Automation?",
        answer:
        "Our platform automatically replies to your Google Play app reviews using AI-generated responses. This saves you time and helps you engage with your users more effectively."
    },
    {
        question: "How does the credit system work?",
        answer:
        "Each automated reply or simulation consumes credits. You can purchase credits anytime in the Billing & Payments section. Credits do not expire."
    },
    {
        question: "What happens when I run out of credits?",
        answer:
        "When your credits drop below 10, the automation will pause automatically. You can purchase additional credits to resume the automation."
    },
    {
        question: "How do I add a new app for automation?",
        answer:
        "Go to your Dashboard, click 'Add App,' and follow the steps to connect your Google Play app to our platform."
    },
    {
        question: "Can I customize the AI responses?",
        answer:
        "Yes. You can set a custom Developer Prompt for each app to guide the AI in generating responses."
    },
    {
        question: "What is a simulation?",
        answer:
        "Simulation allows you to preview AI-generated responses based on the prompt you set before enabling automation. You are required to run at least 10 simulations per app to ensure response quality."
    },
    {
        question: "Can I pause/stop automation anytime?",
        answer:
        "Yes. You can pause or stop the automation at any time from your Dashboard. Once stopped, no credits will be consumed until you resume."
    },
    {
        question: "What happens if I remove an app?",
        answer:
        "Once removed, automation for that app will stop immediately, and all related data will be deleted within 1 hour."
    },
    {
        question: "What permissions are required?",
        answer:
        "We require 2 permissions for the user [robito-service@review-automation-467010.iam.gserviceaccount.com] to enable automation for each app:",
        points: [
        "[View app information and download bulk reports (read-only)] – This permission is needed to fetch the latest reviews and the app's version.",
        "[Reply to reviews] – This permission is required to post replies to reviews."
        ]
    },
    {
        question: "How can I get credits?",
        answer:
        "You can purchase credits directly from the Billing & Payments section of your dashboard. Credits do not expire and can be used for automated replies or simulations."
    },
    {
        question: "What payment methods do you support?",
        answer:
        "We use Xendit as our payment gateway, which processes transactions in IDR. Prices shown in USD are estimates and may vary slightly due to exchange rates. Payment methods may vary by country, but credit cards are supported in most regions."
    },
    {
        question: "Is this Google compliant?",
        answer:
        "Yes. Our system uses the official Google Play Developer API to fetch and reply to reviews. We strictly follow Google’s rate limits, ensure all responses are meaningful and non-spammy, and fully comply with Google Play Developer policies and API terms."
    },
    {
        question: "How can I contact support?",
        answer:
        "You can reach us at developer@robito.ai. We usually reply within 1-3 work days."
    }
  ];

  useEffect(() => {
    if (id && !isNaN(id)) {
      const index = parseInt(id, 10) - 1;
      if (index >= 0 && index < faqs.length) {
        setOpenIndex(index);
        setTimeout(() => {
          faqRefs.current[index]?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 300);
      }
    }
  }, [id, faqs.length]);

  const filteredFaqs = faqs.filter(
    (faq) =>
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (faq.points &&
        faq.points.some((p) => p.toLowerCase().includes(searchQuery.toLowerCase())))
  );

  return (
    <div style={{ padding: "20px" }}>
      <h1 style={{ fontSize: "1.8rem", marginBottom: "10px", color: "#1f2937" }}>
        Help & FAQ
      </h1>
      <p style={{ fontSize: "1rem", marginBottom: "20px", color: "#4b5563" }}>
        Find answers to the most common questions about our platform.
      </p>

      {/* Search Bar */}
      <input
        type="text"
        placeholder="Search FAQ..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        style={searchInputStyle}
      />

      <div style={{ marginTop: "20px", ...faqContainer }}>
        {filteredFaqs.length > 0 ? (
          filteredFaqs.map((faq, index) => (
            <div
              key={index}
              ref={(el) => (faqRefs.current[index] = el)}
              style={{
                ...faqItemStyle,
                border: openIndex === index ? "2px solid #2563eb" : "1px solid #ddd",
              }}
            >
              <div
                style={faqQuestionStyle}
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
              >
                <span>{faq.question}</span>
                <span>{openIndex === index ? "−" : "+"}</span>
              </div>
              {openIndex === index && (
                <div style={faqAnswerStyle}>
                  <p>{faq.answer}</p>
                  {faq.points && (
                    <ul style={faqListStyle}>
                      {faq.points.map((point, i) => (
                        <li key={i}>{point}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          ))
        ) : (
          <p style={{ color: "#6b7280", fontSize: "0.95rem" }}>
            No results found for "{searchQuery}".
          </p>
        )}
      </div>
    </div>
  );
}

/* --- Styles --- */
const faqContainer = {
  display: "flex",
  flexDirection: "column",
  gap: "10px",
};

const faqItemStyle = {
  backgroundColor: "#fff",
  borderRadius: "6px",
  padding: "15px",
  cursor: "pointer",
  boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)",
  transition: "border 0.2s ease",
};

const faqQuestionStyle = {
  display: "flex",
  justifyContent: "space-between",
  fontWeight: "600",
  fontSize: "1rem",
  color: "#1f2937",
};

const faqAnswerStyle = {
  marginTop: "10px",
  fontSize: "0.95rem",
  color: "#4b5563",
};

const faqListStyle = {
  marginTop: "8px",
  paddingLeft: "20px",
  fontSize: "0.9rem",
  color: "#374151",
};

const searchInputStyle = {
  width: "100%",
  padding: "10px",
  border: "1px solid #ccc",
  borderRadius: "6px",
  fontSize: "1rem",
};

